import { Box, ConfirmationDialogBox } from '@atgfr8/atg-ui-tailwind';
import { ToastType } from '@components/Toast/Toast';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import { getLTLFalveyPricingQuotes } from '@store/ltl/actions';
import { setLtlQuoteState } from '@store/ltl/store';
import {
  orderStore,
  removeVendorFromOrder,
  saveOrderDetails,
  setOrderStore,
  voidVendor,
} from '@store/orders';
import { isFalveyInsuranceCommitted } from '@store/orders/helper';
import { AlertIcon, CloseCircleIcon } from '@atgfr8/icons';
import { OrderLineItem } from '@typeDefinitions/lineItemTypes';
import { VendorContractViewModel } from '@typeDefinitions/vendorTypes';
import { formatAmount, handleToast } from '@utils/utils';
import { Show } from 'solid-js';

import classes from './classes';

const checkVendorTabError = (index: number) => {
  //Note: adding this to show the error on the vendor tab for falvey insurance
  if (
    orderStore.vendorFalveyInsuranceError &&
    orderStore.order.vendors &&
    orderStore.order.vendors[index].name === 'Falvey Shippers Insurance'
  )
    return true;
  for (const key in orderStore.orderFormError) {
    const newKey = key.split('.')[0];
    if (newKey === `vendors[${index}]`) return true;
  }
};

const getVendorPrice = (index: number) => {
  return orderStore.order.vendors
    ? orderStore.order.vendors[index].lineItems?.reduce(
        (acc, item) => acc + Number(item.rate) * Number(item.quantity),
        0,
      )
    : 0;
};

export const VendorTab = (
  index: number,
  item: VendorContractViewModel | undefined,
  setTabIndex?: (index: number) => void,
) => {
  const isVoidVendorsEnabled = isFeatureFlagEnabled(
    FeatureFlags.FeatureVoidVendors,
  );

  const removeCustomerPayLineItem = (
    lineItems: OrderLineItem[],
  ): OrderLineItem[] => {
    const falveyCPIndex = lineItems.findIndex(
      (lineItem) =>
        lineItem.type === 'LTL Insurance' &&
        lineItem.operationType !== 'Delete',
    );

    if (falveyCPIndex !== -1) {
      if (lineItems[falveyCPIndex].operationType === 'Insert') {
        lineItems.splice(falveyCPIndex, 1);
      } else {
        lineItems[falveyCPIndex] = {
          ...lineItems[falveyCPIndex],
          operationType: 'Delete',
        };
      }
    }
    return lineItems;
  };

  const handleVoidVendor = async (forceDelete = false) => {
    try {
      const succeeded = Boolean(
        await voidVendor(item?.id as number, index, forceDelete),
      );
      if (!forceDelete && isVoidVendorsEnabled()) {
        Number(item?.id) > 0 && handleToast(ToastType.Success, 'Vendor Voided');
      } else if (succeeded) {
        setTabIndex?.(0);
      }
      return succeeded;
    } catch (error) {
      handleToast(ToastType.Error, (error as Error).message);
      return false;
    }
  };

  const handleVendorRemoval = async () => {
    if (item?.id !== 0) {
      if (!(await handleVoidVendor(true))) return;
      await saveOrderDetails();
    } else {
      removeVendorFromOrder(index);
      setTabIndex?.(0);
    }
    setLtlQuoteState('declaredValue', undefined);
  };

  const updateLoadDeclaredValue = async () => {
    //This will return index of the first Load with declared value
    const falveyLoadIndex = orderStore.order.loads.findIndex((load) =>
      Boolean(load.ltlQuote?.ltlInsurance),
    );

    if (falveyLoadIndex !== -1) {
      await getLTLFalveyPricingQuotes(
        {
          declaredValue: 0,
          ltlQuoteId: orderStore.order.loads[falveyLoadIndex].ltlQuote
            ? String(orderStore.order.loads[falveyLoadIndex].ltlQuote.id)
            : undefined,
          quoteParametersId:
            orderStore.order.loads[falveyLoadIndex].ltlQuoteParameters?.id,
        },
        true,
      );
      setOrderStore(
        'order',
        'loads',
        falveyLoadIndex,
        'ltlQuote',
        'ltlInsurance',
        'shippingSumInsured',
        0,
      );
    }
  };

  const removeFalveyInsurance = async () => {
    // Remove CP from order
    const lineItems = removeCustomerPayLineItem([
      ...orderStore.order.lineItems,
    ]);
    setOrderStore('order', 'lineItems', lineItems);

    // Remove vendor from order
    await handleVendorRemoval();
    // Update load declared value
    await updateLoadDeclaredValue();
  };

  const removeVendorTabHidden = () => {
    if (item?.name === 'Reliance Partners, LLC') {
      return false;
    }
    if (item?.name === 'Falvey Shippers Insurance') {
      return (
        (item.status === 'Open' || item.id === 0) &&
        !isFalveyInsuranceCommitted()
      );
    }
    return item?.status === 'Open' || item?.id === 0;
  };

  return (
    <Box
      class={`flex items-center gap-2 rounded-t-lg px-1.25 py-1.25 ${
        item?.status === 'Void' ? 'bg-[#F4BDBD]' : ''
      }`}
    >
      <Show when={checkVendorTabError(index)}>
        <Box class={classes.errorChildBox}>
          <AlertIcon color="#B00020" />
        </Box>
      </Show>
      <Box class={classes.tabChildBox}>
        <p
          class={`${
            checkVendorTabError(index) ? 'text-[#B00020]!' : 'text-[#0E2C3B]'
          } text-sm font-medium]`}
        >
          {item?.name !== '' ? item?.name : 'VENDOR'}{' '}
          <Show when={item?.status === 'Void'}>
            <span class={classes.colorRed}>(Voided)</span>
          </Show>
        </p>
        <p
          class={`${
            checkVendorTabError(index) ? 'text-[#B00020]!' : 'text-[#0E2C3B]'
          } text-sm font-medium`}
        >
          {formatAmount(getVendorPrice(index) ?? 0)}
        </p>
      </Box>
      <Show when={removeVendorTabHidden()} fallback={<></>}>
        <CloseCircleIcon
          onClick={(e: MouseEvent) => {
            //This is to prevent the tab click event from firing
            e.stopPropagation();

            if (item?.id === 0 && item.name !== 'Falvey Shippers Insurance') {
              removeVendorFromOrder(index);
              setTabIndex?.(0);
            } else if (
              (item?.name === 'Falvey Shippers Insurance' && item.id === 0) ||
              item?.status === 'Open'
            ) {
              openDialogBox(`voidVendor${index}`);
            }
          }}
        />
      </Show>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(`voidVendor${index}`)}
        closeDialogBox={(_reason) => closeDialogBox(`voidVendor${index}`)}
        dataTestId={`voidVendor${index}`}
        id={`voidVendor${index}`}
        title={
          item?.name === 'Falvey Shippers Insurance'
            ? `Are sure they want to remove the insurance quote from the Order ${
                orderStore.order.id
              }-${orderStore.activeTab.index + 1}?`
            : `Are you sure you want to void vendor ${orderStore.order.id}-${
                orderStore.activeTab.index + 1
              }?`
        }
        onSubmit={async () => {
          if (item?.name === 'Falvey Shippers Insurance') {
            await removeFalveyInsurance();
          } else {
            await handleVoidVendor();
          }
        }}
        onSubmitText="Ok"
      />
    </Box>
  );
};
